/**
 * PATH: packages/bills/src/components/draftHistory.ts
 * FILE_ID: BILLS-PKG-CMP-DRAFTHIST-001
 * PURPOSE: Undo/redo for the bill edit draft. A pure past/present/future
 *          reducer (testable with no React) plus a thin `useDraftHistory`
 *          hook so the panel can offer ⌘Z / ⌘⇧Z + visible Undo/Redo.
 * WHY: The form's fields are CONTROLLED React inputs, so the browser's native
 *      ⌘Z does nothing — a "Pick from PDF" that dumps the wrong value into a
 *      field (operator: dragged the delivery address into Name) was
 *      unrecoverable except by retyping. Edits must be reversible.
 *
 * FLOW:
 *   change ─▶ historyPush (coalesce same-label typing) ─▶ present
 *   ⌘Z     ─▶ historyUndo ─▶ previous present (future grows)
 *   ⌘⇧Z    ─▶ historyRedo ─▶ next present
 *
 * Coalescing: consecutive changes that share a non-null `label` (e.g. each
 * keystroke in one field = `field:vendor_name`) collapse into ONE undo step,
 * so ⌘Z reverts a whole edit, not one character. A discrete action (a pick,
 * a supplier link) passes `coalesce:false` so it's always its own step.
 */

import { useCallback, useState } from 'react';

export interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
  /** Label of the change that produced `present`, for keystroke coalescing. */
  lastLabel: string | null;
}

/** f(present) → a fresh history with empty past/future. */
export function historyInit<T>(present: T): HistoryState<T> {
  return { past: [], present, future: [], lastLabel: null };
}

/**
 * f(state, next, label, coalesce) → state with `next` as present.
 * When `coalesce` and the label matches the last change, replace present in
 * place (no new undo step); otherwise push the old present onto `past`.
 * Any push clears the redo `future`.
 */
export function historyPush<T>(
  state: HistoryState<T>,
  next: T,
  label: string | null,
  coalesce: boolean,
): HistoryState<T> {
  if (coalesce && label !== null && label === state.lastLabel) {
    return { ...state, present: next, future: [] };
  }
  return { past: [...state.past, state.present], present: next, future: [], lastLabel: label };
}

/** f(state) → one step back (no-op when there's nothing to undo). */
export function historyUndo<T>(state: HistoryState<T>): HistoryState<T> {
  if (state.past.length === 0) return state;
  const present = state.past[state.past.length - 1];
  return {
    past: state.past.slice(0, -1),
    present,
    future: [state.present, ...state.future],
    lastLabel: null,
  };
}

/** f(state) → one step forward (no-op when there's nothing to redo). */
export function historyRedo<T>(state: HistoryState<T>): HistoryState<T> {
  if (state.future.length === 0) return state;
  const present = state.future[0];
  return {
    past: [...state.past, state.present],
    present,
    future: state.future.slice(1),
    lastLabel: null,
  };
}

export const canUndo = <T>(s: HistoryState<T>): boolean => s.past.length > 0;
export const canRedo = <T>(s: HistoryState<T>): boolean => s.future.length > 0;

export interface DraftHistory<T> {
  present: T;
  /** Apply a change. `coalesce` defaults to false (a discrete undo step). */
  update: (updater: (prev: T) => T, label?: string | null, coalesce?: boolean) => void;
  /** Replace the whole history (bill switch / after save) — not undoable. */
  reset: (value: T) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

/** React binding around the pure reducer above. */
export function useDraftHistory<T>(initial: T): DraftHistory<T> {
  const [state, setState] = useState<HistoryState<T>>(() => historyInit(initial));

  const update = useCallback(
    (updater: (prev: T) => T, label: string | null = null, coalesce = false) => {
      setState((s) => historyPush(s, updater(s.present), label, coalesce));
    },
    [],
  );
  const reset = useCallback((value: T) => setState(historyInit(value)), []);
  const undo = useCallback(() => setState((s) => historyUndo(s)), []);
  const redo = useCallback(() => setState((s) => historyRedo(s)), []);

  return {
    present: state.present,
    update,
    reset,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
}
